/**
 * Premium configurations and utilities for ApexCharts
 */

export const apexDefaultOptions = {
  chart: {
    fontFamily: 'Inter, sans-serif',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 350,
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  grid: {
    borderColor: 'rgba(226, 232, 240, 0.6)',
    strokeDashArray: 4,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 10,
    },
  },
  xaxis: {
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        colors: '#A3AED0',
        fontSize: '12px',
        fontWeight: '500',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#A3AED0',
        fontSize: '12px',
        fontWeight: '500',
      },
    },
  },
  tooltip: {
    theme: 'light',
    x: {
      show: false,
    },
    style: {
      fontSize: '12px',
      fontFamily: 'Inter',
    },
  },
  states: {
    hover: {
      filter: { type: 'lighten', value: 0.05 },
    },
    active: {
      filter: { type: 'none', value: 0 },
    },
  },
  markers: {
    size: 0,
    hover: { size: 6 },
  },
};

export const donutDefaultOptions = {
  ...apexDefaultOptions,
  chart: {
    ...apexDefaultOptions.chart,
    type: 'donut',
  },
  dataLabels: {
    enabled: true,
    dropShadow: { enabled: false },
  },
  plotOptions: {
    pie: {
      expandOnClick: true,
      donut: {
        size: '70%',
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: '14px',
            fontWeight: 600,
            color: '#64748B',
          },
          value: {
            show: true,
            fontSize: '20px',
            fontWeight: 700,
            color: '#1E293B',
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '14px',
            fontWeight: 600,
            color: '#64748B',
          },
        },
      },
    },
  },
  legend: {
    show: true,
    position: 'bottom',
    fontSize: '12px',
    fontWeight: 500,
    labels: {
      colors: '#64748B',
    },
    markers: {
      width: 8,
      height: 8,
      radius: 12,
    },
  },
  stroke: {
    show: true,
    width: 2,
    colors: ['transparent'],
  },
};

export const areaDefaultOptions = {
  ...apexDefaultOptions,
  chart: {
    ...apexDefaultOptions.chart,
    type: 'area',
    sparkline: { enabled: false },
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.5,
      opacityTo: 0.1,
      stops: [0, 90, 100],
    },
  },
  dataLabels: {
    enabled: false,
  },
  xaxis: {
    ...apexDefaultOptions.xaxis,
    tooltip: { enabled: false },
  },
  yaxis: {
    ...apexDefaultOptions.yaxis,
  },
};

export const getGlowOptions = (color) => ({
  chart: {
    dropShadow: {
      enabled: true,
      top: 2,
      left: 0,
      blur: 10,
      opacity: 0.15,
      color: color || '#3965FF',
    },
  },
});

export const getGradientFill = (colors) => ({
  type: 'gradient',
  gradient: {
    shade: 'light',
    type: 'vertical',
    shadeIntensity: 0.5,
    gradientToColors: colors,
    inverseColors: true,
    opacityFrom: 0.85,
    opacityTo: 0.55,
    stops: [0, 100],
  },
});

export const getRadialGradientFill = (color) => ({
  type: 'gradient',
  gradient: {
    shade: 'dark',
    type: 'horizontal',
    shadeIntensity: 0.5,
    gradientToColors: [color],
    inverseColors: true,
    opacityFrom: 1,
    opacityTo: 1,
    stops: [0, 100],
  },
});

export const getTierColors = (value) => {
  if (value >= 90) return { main: '#F59E0B', gradient: ['#FCD34D', '#F59E0B'] }; // Gold
  if (value >= 75) return { main: '#94A3B8', gradient: ['#CBD5E1', '#94A3B8'] }; // Silver
  return { main: '#D97706', gradient: ['#FDBA74', '#D97706'] }; // Bronze
};
